/**
 * Analytics Service
 * Centralized PostHog event tracking with type safety
 *
 * Usage:
 *   import { analytics } from '@/services/analytics';
 *   analytics.track.challengeJoined({ challengeId: '123', stakeAmount: 10 });
 */

import posthog from 'posthog-js';

// ============================================
// Event Name Constants
// ============================================

export const ANALYTICS_EVENTS = {
  // Onboarding
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  PROFILE_CREATED: 'profile_created',

  // Authentication
  LOGIN_STARTED: 'login_started',
  LOGIN_COMPLETED: 'login_completed',
  LOGOUT: 'logout',

  // Challenges
  CHALLENGE_VIEWED: 'challenge_viewed',
  CHALLENGE_JOINED: 'challenge_joined',
  CHALLENGE_CREATED: 'challenge_created',
  CHALLENGE_COMPLETED: 'challenge_completed',
  CHALLENGE_FAILED: 'challenge_failed',
  CHALLENGE_LEFT: 'challenge_left',

  // Activities
  ACTIVITY_LOGGED: 'activity_logged',
  ACTIVITY_SYNCED: 'activity_synced',

  // Financial
  STAKE_DEPOSITED: 'stake_deposited',
  WALLET_FUNDED: 'wallet_funded',
  WITHDRAWAL_INITIATED: 'withdrawal_initiated',
  PAYOUT_RECEIVED: 'payout_received',

  // Integrations
  STRAVA_CONNECT_STARTED: 'strava_connect_started',
  STRAVA_CONNECTED: 'strava_connected',
  STRAVA_DISCONNECTED: 'strava_disconnected',
  STRAVA_SYNC_COMPLETED: 'strava_sync_completed',

  // Engagement
  SHARE_INITIATED: 'share_initiated',
  NOTIFICATION_ENABLED: 'notification_enabled',
  FARCASTER_ADDED: 'farcaster_added',

  // Errors
  ERROR_OCCURRED: 'error_occurred',
} as const;

// ============================================
// Event Property Types
// ============================================

interface OnboardingStepProps {
  step: number;
  stepName: string;
}

interface ProfileCreatedProps {
  hasAvatar: boolean;
  usernameLength: number;
}

interface ChallengeViewedProps {
  challengeId: string;
  challengeName: string;
  challengeType: string;
  stakeAmount: number;
  source?: 'home' | 'discover' | 'deeplink' | 'share';
}

interface ChallengeJoinedProps {
  challengeId: string;
  challengeName: string;
  challengeType: string;
  stakeAmount: number;
  durationDays: number;
  dailyGoal: number;
}

interface ChallengeCreatedProps {
  challengeId: string;
  challengeType: string;
  stakeAmount: number;
  durationDays: number;
  dailyGoal: number;
  isPrivate: boolean;
}

interface ChallengeCompletedProps {
  challengeId: string;
  successRate: number;
  daysCompleted: number;
  totalDays: number;
  amountWon: number;
}

interface ChallengeFailedProps {
  challengeId: string;
  daysCompleted: number;
  totalDays: number;
  amountLost: number;
}

interface ActivityLoggedProps {
  challengeId: string;
  distance: number;
  source: 'manual' | 'strava';
  activityType: string;
}

interface StakeDepositedProps {
  challengeId: string;
  amount: number;
  currency: string;
}

interface WalletFundedProps {
  amount: number;
  method: 'card' | 'crypto' | 'transfer';
}

interface WithdrawalProps {
  amount: number;
}

interface PayoutReceivedProps {
  challengeId: string;
  amount: number;
}

interface StravaSyncProps {
  activitiesCount: number;
  success: boolean;
}

interface ShareInitiatedProps {
  platform: 'twitter' | 'farcaster' | 'instagram' | 'copy_link' | 'native';
  contentType: 'challenge' | 'achievement' | 'profile';
  challengeId?: string;
}

interface ErrorOccurredProps {
  errorType: string;
  errorMessage: string;
  context?: string;
}

// ============================================
// Core Track Function
// ============================================

function track<T extends object>(eventName: string, properties?: T) {
  try {
    posthog.capture(eventName, {
      ...(properties as object),
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error('[Analytics] Failed to track event:', eventName, error);
  }
}

// ============================================
// Typed Event Trackers
// ============================================

export const analytics = {
  /**
   * Raw track function for custom events
   */
  trackRaw: track,

  /**
   * Typed event tracking functions
   */
  track: {
    // Onboarding
    onboardingStarted: () =>
      track(ANALYTICS_EVENTS.ONBOARDING_STARTED),

    onboardingStepCompleted: (props: OnboardingStepProps) =>
      track(ANALYTICS_EVENTS.ONBOARDING_STEP_COMPLETED, props),

    onboardingCompleted: () =>
      track(ANALYTICS_EVENTS.ONBOARDING_COMPLETED),

    profileCreated: (props: ProfileCreatedProps) =>
      track(ANALYTICS_EVENTS.PROFILE_CREATED, props),

    // Authentication
    loginStarted: () =>
      track(ANALYTICS_EVENTS.LOGIN_STARTED),

    loginCompleted: (method: 'email' | 'google') =>
      track(ANALYTICS_EVENTS.LOGIN_COMPLETED, { method }),

    logout: () =>
      track(ANALYTICS_EVENTS.LOGOUT),

    // Challenges
    challengeViewed: (props: ChallengeViewedProps) =>
      track(ANALYTICS_EVENTS.CHALLENGE_VIEWED, props),

    challengeJoined: (props: ChallengeJoinedProps) =>
      track(ANALYTICS_EVENTS.CHALLENGE_JOINED, props),

    challengeCreated: (props: ChallengeCreatedProps) =>
      track(ANALYTICS_EVENTS.CHALLENGE_CREATED, props),

    challengeCompleted: (props: ChallengeCompletedProps) =>
      track(ANALYTICS_EVENTS.CHALLENGE_COMPLETED, props),

    challengeFailed: (props: ChallengeFailedProps) =>
      track(ANALYTICS_EVENTS.CHALLENGE_FAILED, props),

    challengeLeft: (challengeId: string) =>
      track(ANALYTICS_EVENTS.CHALLENGE_LEFT, { challengeId }),

    // Activities
    activityLogged: (props: ActivityLoggedProps) =>
      track(ANALYTICS_EVENTS.ACTIVITY_LOGGED, props),

    activitySynced: (challengeId: string, count: number) =>
      track(ANALYTICS_EVENTS.ACTIVITY_SYNCED, { challengeId, activitiesCount: count }),

    // Financial
    stakeDeposited: (props: StakeDepositedProps) =>
      track(ANALYTICS_EVENTS.STAKE_DEPOSITED, props),

    walletFunded: (props: WalletFundedProps) =>
      track(ANALYTICS_EVENTS.WALLET_FUNDED, props),

    withdrawalInitiated: (props: WithdrawalProps) =>
      track(ANALYTICS_EVENTS.WITHDRAWAL_INITIATED, props),

    payoutReceived: (props: PayoutReceivedProps) =>
      track(ANALYTICS_EVENTS.PAYOUT_RECEIVED, props),

    // Integrations
    stravaConnectStarted: () =>
      track(ANALYTICS_EVENTS.STRAVA_CONNECT_STARTED),

    stravaConnected: (success: boolean) =>
      track(ANALYTICS_EVENTS.STRAVA_CONNECTED, { success }),

    stravaDisconnected: () =>
      track(ANALYTICS_EVENTS.STRAVA_DISCONNECTED),

    stravaSyncCompleted: (props: StravaSyncProps) =>
      track(ANALYTICS_EVENTS.STRAVA_SYNC_COMPLETED, props),

    // Engagement
    shareInitiated: (props: ShareInitiatedProps) =>
      track(ANALYTICS_EVENTS.SHARE_INITIATED, props),

    notificationEnabled: (type: 'push' | 'email') =>
      track(ANALYTICS_EVENTS.NOTIFICATION_ENABLED, { type }),

    farcasterAdded: () =>
      track(ANALYTICS_EVENTS.FARCASTER_ADDED),

    // Errors
    errorOccurred: (props: ErrorOccurredProps) =>
      track(ANALYTICS_EVENTS.ERROR_OCCURRED, props),
  },

  /**
   * Set user properties (persistent traits)
   */
  setUserProperties: (properties: Record<string, unknown>) => {
    try {
      posthog.people.set(properties);
    } catch (error) {
      console.error('[Analytics] Failed to set user properties:', error);
    }
  },

  /**
   * Increment a numeric user property
   * Note: PostHog JS doesn't have increment, so we use capture with $set
   */
  incrementUserProperty: (property: string, value: number = 1) => {
    try {
      // PostHog doesn't support increment in JS SDK directly
      // Use capture with special $set property instead
      posthog.capture('$set', {
        $set: { [property]: value },
      });
    } catch (error) {
      console.error('[Analytics] Failed to increment property:', error);
    }
  },
};

export default analytics;
